/** Disabled Azure account only — do not list stage or prod active accounts here. */
const DISABLED_LEGACY_HOSTS = new Set([
  'aiq65prod.blob.core.windows.net',
]);

const ACTIVE_BLOB_HOST =
  process.env.REACT_APP_BLOB_HOST || 'aiq65prods.blob.core.windows.net';

const ACTIVE_BLOB_CONTAINER =
  process.env.REACT_APP_BLOB_CONTAINER || 'aiq65productimages';

const KNOWN_ACTIVE_HOSTS = new Set([
  'ai65stage.blob.core.windows.net',
  'aiq65prods.blob.core.windows.net',
  'aiq65stage.blob.core.windows.net',
  ACTIVE_BLOB_HOST,
]);

/**
 * Decode a blob path until it stops changing.
 *
 * This used to stop after six passes. Product image names in this database are
 * up to TWENTY-NINE encodings deep — an upload path that re-encoded an already
 * encoded filename on every save, so `%25` became `%2525` became `%252525`.
 * Stopping at six produced a path that was still encoded, i.e. a URL that
 * pointed at nothing.
 *
 * The ceiling is a runaway guard, not a policy: it is far above anything real,
 * and reaching it means something is wrong with the input rather than with the
 * number.
 */
function normalizeBlobPath(pathname) {
  let path = pathname;
  for (let i = 0; i < 64; i += 1) {
    let decoded;
    try {
      decoded = decodeURIComponent(path.replace(/\+/g, ' '));
    } catch {
      // Not valid percent-encoding any more — whatever we have is as good as
      // it gets, and it is better than nothing.
      break;
    }
    if (decoded === path) break;
    path = decoded;
  }
  return path;
}

function remapContainerPath(pathname) {
  const parts = pathname.replace(/^\/+/, '').split('/');
  if (parts.length && parts[0] === 'aiq65productimages' && parts[0] !== ACTIVE_BLOB_CONTAINER) {
    parts[0] = ACTIVE_BLOB_CONTAINER;
    return `/${parts.join('/')}`;
  }
  return pathname;
}

/**
 * Multiply-encoded, i.e. `%25` where a single `%` was meant.
 *
 * Kept as a name because it reads well at the call sites, but it no longer
 * means "unusable". These URLs decode back to a real path — see
 * normalizeBlobPath — so they are repaired rather than discarded.
 */
function isMultiplyEncoded(url) {
  return typeof url === 'string' && url.includes('%2525');
}

/**
 * Rewrite only disabled legacy hosts (aiq65prod). URLs already on stage/prod are unchanged.
 */
export function rewriteBlobUrl(url) {
  if (!url || typeof url !== 'string' || !url.includes('blob.core.windows.net')) {
    return url;
  }

  // Deliberately NOT an early return any more. Bailing here left the admin's
  // edit form with the raw multiply-encoded URL, which loads nothing; these are
  // exactly the ones that need normalising most.
  try {
    const parsed = new URL(url);

    if (KNOWN_ACTIVE_HOSTS.has(parsed.host)) {
      parsed.pathname = normalizeBlobPath(parsed.pathname);
      return parsed.toString();
    }

    if (DISABLED_LEGACY_HOSTS.has(parsed.host)) {
      parsed.host = ACTIVE_BLOB_HOST;
      let path = normalizeBlobPath(parsed.pathname);
      path = remapContainerPath(path);
      parsed.pathname = path;
      return parsed.toString();
    }

    parsed.pathname = normalizeBlobPath(parsed.pathname);
    return parsed.toString();
  } catch {
    return url;
  }
}

export const blobSrc = rewriteBlobUrl;

/**
 * Every usable image URL for a product.
 *
 * The filter here used to DROP any multiply-encoded URL. In this database that
 * is 86 of 1000 product images — and dropping them is why the admin's edit form
 * showed no image at all for those products, which read as "the blob did not
 * load". They are now normalised like every other URL instead of thrown away.
 */
export function getProductImageUrls(product) {
  const urls = product?.image_urls || product?.images || [];
  const list = Array.isArray(urls) ? urls : [urls];
  return list.filter(Boolean).map(rewriteBlobUrl);
}

/** Exported for diagnostics: how many of these were previously discarded. */
export function countMultiplyEncoded(product) {
  const urls = product?.image_urls || product?.images || [];
  const list = Array.isArray(urls) ? urls : [urls];
  return list.filter(isMultiplyEncoded).length;
}

export function rewriteBlobUrlsInData(data) {
  if (typeof data === 'string') {
    return data.includes('blob.core.windows.net') ? rewriteBlobUrl(data) : data;
  }

  if (Array.isArray(data)) {
    return data.map(rewriteBlobUrlsInData);
  }

  /*
   * Binary is not a bag of keys.
   *
   * A Blob, a File, an ArrayBuffer and a typed array are all `typeof 'object'`
   * with no own enumerable properties, so the branch below rebuilt them as `{}`
   * -- silently. A downloaded spreadsheet arrived as a file containing the
   * eleven characters "[object Object]", which looks like the export being
   * broken rather than the response being rewritten on the way through.
   *
   * Anything that is not a plain object or array is returned untouched: there
   * are no blob URLs inside a byte stream to rewrite.
   */
  if (typeof Blob !== 'undefined' && data instanceof Blob) return data;
  if (typeof ArrayBuffer !== 'undefined' && (data instanceof ArrayBuffer || ArrayBuffer.isView(data))) {
    return data;
  }

  if (data && typeof data === 'object') {
    const proto = Object.getPrototypeOf(data);
    // Only walk plain objects. A Date, a Map or anything else with a prototype
    // of its own comes out the other side as a different kind of thing.
    if (proto !== Object.prototype && proto !== null) return data;

    return Object.fromEntries(
      Object.entries(data).map(([key, value]) => [key, rewriteBlobUrlsInData(value)])
    );
  }

  return data;
}
